import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User as UserIcon, Eye, EyeOff } from "lucide-react";
import { authAPI } from "../api";
import GoogleSignInButton from "../components/GoogleSignInButton";
import AuthShell from "../components/AuthShell";
import Button from "../components/atoms/Button";
import { Field, InputWithIcon, inputStyle, ErrorBox, Divider } from "./Login";

export default function Signup({ onLogin }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim() || !password.trim()) { setError("Please fill in all fields."); return; }
    if (!/\S+@\S+\.\S+/.test(email))    { setError("Please enter a valid email address."); return; }
    if (password.length < 6)            { setError("Password must be at least 6 characters."); return; }
    if (password !== confirmPassword)   { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      const data = await authAPI.signup(name, email, password);
      onLogin(data.user, data.token);
      navigate(localStorage.getItem("onboarded") === "true" ? "/dashboard" : "/onboarding");
    } catch (err) { setError(err.message || "Something went wrong."); }
    finally { setLoading(false); }
  }

  return (
    <AuthShell
      quote={<>Twelve weeks. Four levels. <em>One steady climb.</em></>}
      attribution="The plan"
    >
      <h1 className="font-display" style={{ fontSize: 48, lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "0 0 8px" }}>
        Create your <em style={{ color: "var(--gold-500)" }}>account.</em>
      </h1>
      <p style={{ color: "var(--fg-3)", fontSize: 15, margin: "0 0 28px" }}>
        Twelve free lessons. No card required.
      </p>

      {error && <ErrorBox>{error}</ErrorBox>}

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Field id="name" label="Your name">
          <InputWithIcon icon={<UserIcon size={18} strokeWidth={1.5} />}>
            <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Vinothkumar" style={inputStyle} />
          </InputWithIcon>
        </Field>

        <Field id="email" label="Email">
          <InputWithIcon icon={<Mail size={18} strokeWidth={1.5} />}>
            <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" style={inputStyle} />
          </InputWithIcon>
        </Field>

        <GoogleSignInButton
          label="Sign up with Google"
          onSuccess={(user, token) => {
            onLogin(user, token);
            navigate(localStorage.getItem("onboarded") === "true" ? "/dashboard" : "/onboarding");
          }}
          onError={(msg) => setError(msg)}
        />

        <Divider>or set a password</Divider>

        <Field id="password" label="Password" hint="Six characters or more.">
          <InputWithIcon
            icon={<Lock size={18} strokeWidth={1.5} />}
            trailing={
              <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Hide password" : "Show password"}
                style={{ background: "transparent", border: 0, color: "var(--fg-3)", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
                {showPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
              </button>
            }
          >
            <input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" style={inputStyle} />
          </InputWithIcon>
        </Field>

        <Field id="confirmPassword" label="Confirm password">
          <InputWithIcon icon={<Lock size={18} strokeWidth={1.5} />}>
            <input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" style={inputStyle} />
          </InputWithIcon>
        </Field>

        <Button type="submit" variant="primary" disabled={loading} style={{ width: "100%", justifyContent: "center", marginTop: 4 }}>
          {loading ? "Creating account…" : "Create account"}
        </Button>
      </form>

      <p style={{ marginTop: 28, color: "var(--fg-3)", fontSize: 14, textAlign: "center" }}>
        Already have one?{" "}
        <Link to="/login" style={{ color: "var(--gold-700)", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid currentColor" }}>
          Sign in →
        </Link>
      </p>
    </AuthShell>
  );
}
